import { useParams, Link, Navigate } from 'react-router-dom';
import { SignIn, useUser } from '@clerk/clerk-react';
import { useTenantBranding } from '../services/hooks/useTenants';

export default function TenantLoginPage() {
  const { tenantCode } = useParams<{ tenantCode: string }>();
  const { isSignedIn, isLoaded } = useUser();
  const { data: branding, isLoading, isError } = useTenantBranding(tenantCode ?? '');

  // Si ya está autenticado, mandamos a /:tenantCode y que el destino lo decida el
  // rol: `ProtectedOperatorRoute` envía a los clientes a `mi-calendario` y al resto
  // al dashboard. Apuntar directo a /dashboard rebotaba a los clientes, que no
  // tienen acceso a esa ruta.
  if (isLoaded && isSignedIn) {
    return <Navigate to={`/${tenantCode}`} replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          <span className="text-sm">Cargando...</span>
        </div>
      </div>
    );
  }

  if (isError || !branding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-gray-700 font-medium">Organización no encontrada</p>
          <p className="text-sm text-gray-500">
            El código <span className="font-mono bg-gray-100 px-1 rounded">{tenantCode}</span> no corresponde a ninguna organización activa.
          </p>
          <Link to="/login" className="text-sm text-blue-600 hover:underline">
            Volver al inicio de sesión
          </Link>
        </div>
      </div>
    );
  }

  const primaryColor = branding.metadata.colors?.primary || '#000000';
  const logo = branding.metadata.logo;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-6 px-4">
      {/* Branding del tenant */}
      <div className="flex flex-col items-center gap-3">
        {logo && (
          <img
            src={logo}
            alt={`Logo ${branding.name}`}
            className="h-16 w-auto object-contain"
          />
        )}
        <h1 className="text-xl font-semibold text-gray-800">{branding.name}</h1>
      </div>

      {/* Formulario de Clerk con colores del tenant */}
      <SignIn
        path={`/${tenantCode}/login`}
        routing="path"
        forceRedirectUrl={`/${tenantCode}`}
        signUpUrl={`/${tenantCode}/accept-invitation`}
        appearance={{
          variables: {
            colorPrimary: primaryColor,
          },
        }}
      />
    </div>
  );
}
